import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ListMusic, Loader2, Play, Shuffle, Eye } from "lucide-react";
import { TrackTable } from "@/components/TrackTable";
import { EmptyState } from "@/components/PageHeader";
import { usePlayer } from "@/context/PlayerContext";
import { api, fmtTime, registerShared, registerSharedAlbums } from "@/lib/api";
import { Mosaic } from "@/pages/Playlists";

export const SharedWithYou = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/shared/playlists").then((r) => {
      r.data.forEach((p) => registerSharedAlbums(p.id, p.cover_album_ids));
      setItems(r.data);
    }).catch(() => setItems([]));
  }, []);
  if (!items?.length) return null;
  return (
    <section className="mt-14" data-testid="shared-with-you">
      <h2 className="mb-1 font-heading text-2xl font-bold tracking-tight">Shared with you</h2>
      <p className="mb-6 text-xs text-zinc-500">Listen-only playlists from the owner's library.</p>
      <div className="grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {items.map((p) => (
          <Link key={p.id} to={`/shared/${p.id}`} data-testid={`shared-card-${p.id}`} className="group">
            <div className="overflow-hidden rounded-md border border-zinc-200 transition-transform duration-300 group-hover:-translate-y-1"><Mosaic ids={p.cover_album_ids} /></div>
            <p className="mt-3 truncate text-sm font-medium">{p.name}</p>
            <p className="truncate font-mono text-[11px] text-zinc-500">{p.track_count} songs · from {p.owner_name || "owner"}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default function SharedPlaylist() {
  const { id } = useParams();
  const [pl, setPl] = useState(null);
  const p = usePlayer();
  useEffect(() => {
    setPl(null);
    api.get(`/shared/playlists/${id}`).then((r) => {
      registerShared(id, r.data.tracks);
      registerSharedAlbums(id, r.data.cover_album_ids);
      setPl(r.data);
    }).catch(() => setPl(false));
  }, [id]);
  if (pl === false) return <EmptyState icon={ListMusic} title="Playlist not available" text="It may no longer be shared with you." />;
  if (!pl) return <Loader2 className="animate-spin text-zinc-400" />;
  const total = pl.tracks.reduce((s, t) => s + (t.duration || 0), 0);
  return (
    <div className="animate-rise" data-testid="shared-playlist-page">
      <div className="flex flex-col gap-10 md:flex-row md:items-end">
        <div className="w-full max-w-xs shrink-0 overflow-hidden rounded-md shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)] md:w-64"><Mosaic ids={pl.cover_album_ids} /></div>
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-sky-700" data-testid="shared-playlist-badge"><Eye size={12} />Shared by {pl.owner_name || "owner"} · listen only</p>
          <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight sm:text-5xl" data-testid="shared-playlist-title">{pl.name}</h1>
          {pl.description && <p className="mt-3 max-w-xl text-sm text-zinc-600">{pl.description}</p>}
          <p className="mt-2 font-mono text-xs text-zinc-500">{pl.tracks.length} songs · {fmtTime(total)}</p>
          <div className="mt-8 flex items-center gap-3">
            <button onClick={() => p.playTracks(pl.tracks)} disabled={!pl.tracks.length} data-testid="shared-play-button"
              className="flex h-12 items-center gap-2 rounded-full bg-zinc-900 px-6 text-sm font-medium text-white transition-transform hover:scale-[1.03] disabled:opacity-40">
              <Play size={16} fill="currentColor" /> Play
            </button>
            <button onClick={() => p.playTracks([...pl.tracks].sort(() => Math.random() - 0.5))} disabled={!pl.tracks.length} data-testid="shared-shuffle-button"
              className="flex h-12 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900 disabled:opacity-40">
              <Shuffle size={15} /> Shuffle
            </button>
          </div>
        </div>
      </div>
      <div className="mt-14">
        {pl.tracks.length ? <TrackTable tracks={pl.tracks} showCover readOnly /> :
          <EmptyState icon={ListMusic} title="This playlist is empty" text="The owner hasn't added songs yet." />}
      </div>
    </div>
  );
}
